// Master Apps · preferências do catálogo — persistidas em localStorage.
//
// A aba selecionada (Todos/Produção/Homologação/…) e os chips de filtro
// sobrevivem à navegação: sair para o detalhe de um app e voltar não pode
// zerar a seleção que o usuário acabou de montar. Mesmo padrão de persistência
// manual do store de tema (`store/theme.ts`) — leve, sem middleware.
//
// A BUSCA fica de fora de propósito: busca é pergunta do momento, não
// preferência; reaparecer preenchida dias depois só confunde.
import { create } from 'zustand';

/** Abas do catálogo. 'todos' é a padrão — todo status junto, para todo mundo. */
export const CATALOG_VIEWS = [
  'todos',
  'prod',
  'homolog',
  'dev',
  'manutencao',
  'pausado',
  'depreciado',
] as const;
export type CatalogView = (typeof CATALOG_VIEWS)[number];

/**
 * Abas que só o admin enxerga; um colaborador com esse valor herdado cai em 'todos'.
 *
 * `pausado` entra aqui junto de `dev`: projeto parado não tem nada a oferecer ao
 * colaborador — ele não consegue usar, e ver a aba só geraria a pergunta "por
 * que está parado?" para quem não decide isso. Quem precisa da lista é quem
 * prioriza a retomada, que é admin.
 */
export const ADMIN_ONLY_VIEWS: readonly CatalogView[] = [
  'dev',
  'manutencao',
  'pausado',
  'depreciado',
];

interface CatalogPrefsState {
  view: CatalogView;
  typeSel: string[];
  critSel: string[];
  teamSel: string[];
  setView: (v: CatalogView) => void;
  toggleType: (id: string) => void;
  toggleCrit: (id: string) => void;
  toggleTeam: (id: string) => void;
  /** Limpa só os chips — a aba escolhida fica. */
  clearFilters: () => void;
}

const STORAGE_KEY = 'master-apps:catalog-prefs';

interface Persisted {
  view: CatalogView;
  typeSel: string[];
  critSel: string[];
  teamSel: string[];
}

const DEFAULTS: Persisted = { view: 'todos', typeSel: [], critSel: [], teamSel: [] };

function sanitizeList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function load(): Persisted {
  if (typeof localStorage === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Record<keyof Persisted, unknown>>;
    return {
      // Valor desconhecido (versão antiga, edição manual) volta ao padrão.
      view: CATALOG_VIEWS.includes(parsed.view as CatalogView)
        ? (parsed.view as CatalogView)
        : 'todos',
      typeSel: sanitizeList(parsed.typeSel),
      critSel: sanitizeList(parsed.critSel),
      teamSel: sanitizeList(parsed.teamSel),
    };
  } catch {
    return DEFAULTS;
  }
}

function persist(state: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage cheio/bloqueado: a preferência só não sobrevive ao reload.
  }
}

const initial = load();

const toggled = (list: string[], id: string) =>
  list.includes(id) ? list.filter((v) => v !== id) : [...list, id];

export const useCatalogPrefs = create<CatalogPrefsState>((set, get) => {
  const update = (patch: Partial<Persisted>) => {
    const s = get();
    const next: Persisted = {
      view: patch.view ?? s.view,
      typeSel: patch.typeSel ?? s.typeSel,
      critSel: patch.critSel ?? s.critSel,
      teamSel: patch.teamSel ?? s.teamSel,
    };
    persist(next);
    set(next);
  };

  return {
    ...initial,
    setView: (v) => update({ view: v }),
    toggleType: (id) => update({ typeSel: toggled(get().typeSel, id) }),
    toggleCrit: (id) => update({ critSel: toggled(get().critSel, id) }),
    toggleTeam: (id) => update({ teamSel: toggled(get().teamSel, id) }),
    clearFilters: () => update({ typeSel: [], critSel: [], teamSel: [] }),
  };
});
